import React, { useEffect } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Select from '@atlaskit/select';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	ControlsContainer,
	ControlContainer,
	ControlLabel,
} from '@atlassian/jira-software-report-charting-common/src/ui/controls/styled.tsx';
import type {
	SprintReportsEstimationStatistic,
	Sprint,
	EstimationFieldOption,
	SprintOption,
} from '../../common/types';
import messages from './messages';
import AsyncSprintActions from './sprint-actions/async';
import { optionForStatistic, optionForSprint, getSprintFilterEventAttributes } from './utils';

type Props = {
	sprintsLoading: boolean;
	sprints: null | Sprint[];
	sprint: null | Sprint;
	estimationFieldsLoading: boolean;
	estimation: null | SprintReportsEstimationStatistic;
	estimationFields: null | SprintReportsEstimationStatistic[];
	disabled: boolean;
	boardId: number;
	onChangeSprint: (arg1: Sprint) => void;
	onChangeEstimation: (arg1: SprintReportsEstimationStatistic) => void;
	onExperienceSuccess: () => void;
	onEditSprintComplete: (sprint: Sprint) => void;
};

export const SprintDropDownControl = ({
	onChangeSprint,
	sprints,
	disabled,
	sprintsLoading,
	sprint,
	isDefaultOpen,
}: {
	onChangeSprint: (arg1: Sprint) => void;
	sprints: null | Sprint[];
	disabled: boolean;
	sprintsLoading: boolean;
	sprint: null | Sprint;
	isDefaultOpen?: boolean;
}) => {
	const intl = useIntl();

	const onChangeSprintWithAnalytics = (
		option: SprintOption,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		_: any,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		onChangeSprint(option.sprint);
		const attributes: {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			[key: string]: any;
		} = getSprintFilterEventAttributes(sprints, option.sprint);
		fireUIAnalytics(analyticsEvent, 'sprintBurndownChartSprintFilter', attributes);
	};
	return (
		<ControlContainer data-test-id="software-burn-reports-common.ui.controls.sprint-dropdown">
			<ControlLabel htmlFor="burndownSprint">{intl.formatMessage(messages.sprint)}</ControlLabel>
			<Select
				defaultMenuIsOpen={isDefaultOpen}
				isDisabled={disabled}
				id="burndownSprint"
				isLoading={sprintsLoading}
				aria-label={intl.formatMessage(messages.sprint)}
				label={intl.formatMessage(messages.sprint)}
				value={sprint ? optionForSprint(sprint) : null}
				// @ts-expect-error - TS2322 - Type '(option: SprintOption, _: any, analyticsEvent: UIAnalyticsEventInterface) => void' is not assignable to type '(value: SprintOption | null, action: ActionMeta<SprintOption>) => void'.
				onChange={onChangeSprintWithAnalytics}
				options={sprints ? sprints.map(optionForSprint) : []}
			/>
		</ControlContainer>
	);
};

export const EstimationDropDownControl = ({
	onChangeEstimation,
	estimation,
	disabled,
	estimationFieldsLoading,
	estimationFields,
	isDefaultOpen,
}: {
	onChangeEstimation: (arg1: SprintReportsEstimationStatistic) => void;
	estimationFieldsLoading: boolean;
	estimation: null | SprintReportsEstimationStatistic;
	estimationFields: null | SprintReportsEstimationStatistic[];
	disabled: boolean;
	isDefaultOpen?: boolean;
}) => {
	const intl = useIntl();

	const onChangeEstimationWithAnalytics = (
		option: EstimationFieldOption,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		_: any,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		onChangeEstimation(option.value);
		fireUIAnalytics(analyticsEvent, 'sprintBurndownChartEstimationFilter', {
			estimationStatistic: option.value,
		});
	};
	return (
		<ControlContainer data-test-id="software-burn-reports-common.ui.controls.estimation-dropdown">
			<ControlLabel htmlFor="burndownEstimationField">
				{intl.formatMessage(messages.estimationField)}
			</ControlLabel>
			<Select
				defaultMenuIsOpen={isDefaultOpen}
				isDisabled={disabled}
				id="burndownEstimationField"
				aria-label={intl.formatMessage(messages.estimationField)}
				label={intl.formatMessage(messages.estimationField)}
				isLoading={estimationFieldsLoading}
				// @ts-expect-error - TS2322 - Type '(option: EstimationFieldOption, _: any, analyticsEvent: UIAnalyticsEventInterface) => void' is not assignable to type '(value: EstimationFieldOption | null, action: ActionMeta<EstimationFieldOption>) => void'.
				onChange={onChangeEstimationWithAnalytics}
				value={estimation ? optionForStatistic(estimation, intl) : null}
				options={
					estimationFields
						? estimationFields.map((estimationField) => optionForStatistic(estimationField, intl))
						: []
				}
			/>
		</ControlContainer>
	);
};

const Controls = ({
	sprints,
	sprint,
	estimation,
	sprintsLoading,
	estimationFieldsLoading,
	estimationFields,
	onChangeSprint,
	onChangeEstimation,
	disabled,
	onExperienceSuccess,
	boardId,
	onEditSprintComplete,
}: Props) => {
	useEffect(() => {
		if (!sprintsLoading && !estimationFieldsLoading) {
			onExperienceSuccess();
		}
	}, [onExperienceSuccess, sprint, estimation, sprintsLoading, estimationFieldsLoading]);

	return (
		<ControlsContainer>
			<SprintDropDownControl
				onChangeSprint={onChangeSprint}
				sprints={sprints}
				disabled={disabled}
				sprintsLoading={sprintsLoading}
				sprint={sprint}
			/>
			<EstimationDropDownControl
				onChangeEstimation={onChangeEstimation}
				estimation={estimation}
				disabled={disabled}
				estimationFieldsLoading={estimationFieldsLoading}
				estimationFields={estimationFields}
			/>

			{!sprintsLoading && sprint && (
				<AsyncSprintActions
					sprint={sprint}
					boardId={boardId}
					onEditSprintComplete={onEditSprintComplete}
				/>
			)}
		</ControlsContainer>
	);
};

export default Controls;
